import React, { useMemo } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { Button } from "@mui/material";
import styled from "@emotion/styled";

import { formatNumber } from "../../Utils/helpers";

import { CrossMark, File, Out } from "../../Assets/Icons";

const FileSizeConverter = (size, convertTo) => {
  if (convertTo === "KB") {
    return formatNumber(size / 1000);
  } else if (convertTo === "MB") {
    return formatNumber(size / 1000000);
  } else if (convertTo === "GB") {
    return formatNumber(size / 1000000000);
  } else {
    return size;
  }
};

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const FileUploaderField = ({
  name,
  title,
  accept,
  maxSize,
  showFileSizeIn = "MB",
  files = [],
  onFileRemove,
  rules = {},
}) => {
  const { control } = useFormContext();

  const supportedFormatsLabel = useMemo(
    () =>
      accept
        .split(",")
        .map((format) => format.replace(".", "").toUpperCase())
        .join(", "),
    [accept]
  );

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field: { onChange, value, ...fieldProps } }) => {
        const handleDragOver = (e) => {
          if (files.length === 0) {
            e.preventDefault();
          }
        };

        const handleDrop = (e) => {
          if (files.length === 0) {
            e.preventDefault();
            onChange(Array.from(e.dataTransfer.files));
          }
        };

        return (
          <Button
            className="custom-file-uploader"
            component={files?.length === 0 ? "label" : "div"}
            role={undefined}
            variant="text"
            disableRipple
            tabIndex={-1}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
          >
            {files?.length === 0 ? (
              <div className="uploader">
                <div className="uploader-icon">
                  <Out />
                </div>
                <span className="uploader-title">{title}</span>
                <span className="uploader-description">
                  Drag and drop or click to upload<br></br>
                  Supported formats: {supportedFormatsLabel}{" "}
                  {!!maxSize &&
                    `(max. ${FileSizeConverter(maxSize, showFileSizeIn).replace(".00", "")}${showFileSizeIn})`}
                </span>
              </div>
            ) : (
              <div className="selected-files">
                {Array.from(files).map((file, idx) => (
                  <div key={idx} className="selected-file">
                    <div className="file-spec">
                      <div className="icon-wrapper">
                        <File />
                      </div>
                      <div className="file-info">
                        <div className="file-name">{file.name}</div>
                        <p className="file-size">
                          {FileSizeConverter(file.size, showFileSizeIn)}
                          {showFileSizeIn}
                        </p>
                      </div>
                    </div>
                    <div className="actions">
                      <button type="button" className="file-deselect" onClick={() => onFileRemove(idx)}>
                        <CrossMark />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <VisuallyHiddenInput type="file" {...fieldProps} onChange={(e) => onChange(Array.from(e.target.files))} />
          </Button>
        );
      }}
    />
  );
};

export default FileUploaderField;
